import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MentorStoryImage } from "@/lib/api/mentor-stories";
import { StorySlideUpload } from "./story-slide-upload";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2 } from "lucide-react";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";

interface StorySlideEditorProps {
  id: string;
  mentorId: string;
  slide: Partial<MentorStoryImage>;
  index: number;
  onUpdate: (updated: Partial<MentorStoryImage>) => void;
  onDelete: () => void;
  isDeleting?: boolean;
}

export function StorySlideEditor({
  id,
  mentorId,
  slide,
  index,
  onUpdate,
  onDelete,
  isDeleting = false,
}: StorySlideEditorProps) {
  const [eyebrow, setEyebrow] = useState(slide.eyebrow ?? "");
  const [header, setHeader] = useState(slide.header ?? "");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDelete = () => {
    onDelete();
    setShowDeleteDialog(false);
  };

  return (
    <>
      <Card
        ref={setNodeRef}
        style={style}
        className={`border border-gray-300 p-4 dark:border-gray-600 ${isDragging ? "opacity-50 shadow-lg" : "shadow-md"}`}
      >
        <div className="flex gap-3">
          <div
            className="flex cursor-grab items-start pt-1 active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="text-muted-foreground h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Slide {index + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                disabled={isDeleting}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex gap-4">
              <div className="w-1/3 shrink-0">
                <StorySlideUpload
                  mentorId={mentorId}
                  currentImageUrl={slide.url}
                  onUploaded={(key, url) => onUpdate({ key, url })}
                />
              </div>

              <div className="flex min-w-0 flex-1 flex-col gap-4">
                <div>
                  <label className="text-muted-foreground text-xs font-medium">
                    Eyebrow text
                  </label>
                  <input
                    type="text"
                    value={eyebrow}
                    maxLength={40}
                    onChange={(e) => {
                      setEyebrow(e.target.value);
                      onUpdate({ eyebrow: e.target.value });
                    }}
                    className="mt-1 w-full rounded border px-3 py-2 text-sm"
                    placeholder="Short label (e.g. 'Chapter 1')"
                  />
                  <span className="text-muted-foreground/60 float-right text-xs">
                    {eyebrow.length}/40
                  </span>
                </div>

                <div>
                  <label className="text-muted-foreground text-xs font-medium">
                    Header text
                  </label>
                  <input
                    type="text"
                    value={header}
                    maxLength={80}
                    onChange={(e) => {
                      setHeader(e.target.value);
                      onUpdate({ header: e.target.value });
                    }}
                    className="mt-1 w-full rounded border px-3 py-2 text-sm"
                    placeholder="Main heading (e.g. 'Welcome to your journey')"
                  />
                  <span className="text-muted-foreground/60 float-right text-xs">
                    {header.length}/80
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <ConfirmActionDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        title="Delete Slide"
        description={`Are you sure you want to delete slide ${index + 1}? This action cannot be undone.`}
      />
    </>
  );
}
